import React from 'react';
import gql from 'graphql-tag';
import { Mutation } from '@apollo/react-components';
import { useMutation } from '@apollo/react-hooks';
import get from 'lodash/get';
import nanoid from 'nanoid';

import { UniqueWithinTypeEncode } from '../../../apollo/uniqueWithinType';

const favoriteForType = ({ __typename, workId, inventoryItemId }) => {
  switch (__typename) {
    case 'lists_InventoryItemFavorite':
      return { inventoryItemId };
    case 'lists_WorkFavorite':
      return { workId };
    default:
      throw new Error(`Don't know how to favorite type ${__typename}`);
  }
};

const FAVORITE_MUTATION = gql`
  mutation exploreAddFavorite($favorite: lists_FavoriteInput!) {
    lists_favAction: lists_favorite(favorite: $favorite) {
      __typename
      id
      isFavorited
      item {
        __typename
        id
        ... on lists_InventoryItemFavorite {
          inventoryItemId
        }
        ... on lists_WorkFavorite {
          workId
        }
      }
    }
  }
`;

const UNFAVORITE_MUTATION = gql`
  mutation exploreRemoveFavorite($favorite: lists_FavoriteInput!) {
    lists_favAction: lists_unfavorite(favorite: $favorite) {
      __typename
      id
      isFavorited
      item {
        __typename
        id
        ... on lists_InventoryItemFavorite {
          inventoryItemId
        }
        ... on lists_WorkFavorite {
          workId
        }
      }
    }
  }
`;

const toggleFavorite = (mutate, assumeFavorited) => favoriteStatus => {
  const { item } = favoriteStatus || {};
  const favorite = favoriteForType(item);

  return mutate({
    variables: { favorite },
    optimisticResponse: {
      __typename: 'Mutation',
      lists_favAction: {
        ...favoriteStatus,
        isFavorited: assumeFavorited
      }
    }
  });
};

const WORK_IS_FAVORITED_FRAGMENT = gql`
  fragment WorkIsFavoritedFragment on Work {
    isFavorited
  }
`;

export const withAddWorkToFavorites = PassedComponent => {
  const WrappedComponent = props => {
    return (
      <Mutation mutation={FAVORITE_MUTATION}>
        {mutate => (
          <PassedComponent
            {...props}
            addWorkToFavorites={workId =>
              mutate({
                variables: { favorite: { workId } },
                optimisticResponse: {
                  __typename: 'Mutation',
                  lists_favAction: {
                    __typename: 'lists_FavoriteStatus',
                    id: nanoid(),
                    isFavorited: true,
                    item: {
                      __typename: 'lists_WorkFavorite',
                      id: nanoid(),
                      workId
                    }
                  }
                },
                update: (cache, { data }) => {
                  const id = `Work:${UniqueWithinTypeEncode('Work', workId)}`;

                  const isFavorited = get(data, 'lists_favAction.isFavorited');

                  cache.writeFragment({
                    id,
                    fragment: WORK_IS_FAVORITED_FRAGMENT,
                    data: {
                      __typename: 'Work',
                      isFavorited
                    }
                  });
                }
              })
            }
          />
        )}
      </Mutation>
    );
  };

  WrappedComponent.displayName = `withAddWorkToFavorites(${
    PassedComponent.displayName
  })`;

  return WrappedComponent;
};

export const withRemoveWorkFromFavorites = PassedComponent => {
  const WrappedComponent = props => {
    return (
      <Mutation mutation={UNFAVORITE_MUTATION}>
        {mutate => (
          <PassedComponent
            {...props}
            removeWorkFromFavorites={workId =>
              mutate({
                variables: { favorite: { workId } },
                optimisticResponse: {
                  __typename: 'Mutation',
                  lists_favAction: {
                    __typename: 'lists_FavoriteStatus',
                    id: nanoid(),
                    isFavorited: false,
                    item: {
                      __typename: 'lists_WorkFavorite',
                      id: nanoid(),
                      workId
                    }
                  }
                },
                update: (cache, { data }) => {
                  const id = `Work:${UniqueWithinTypeEncode('Work', workId)}`;

                  const isFavorited = get(data, 'lists_favAction.isFavorited');

                  cache.writeFragment({
                    id,
                    fragment: WORK_IS_FAVORITED_FRAGMENT,
                    data: {
                      __typename: 'Work',
                      isFavorited
                    }
                  });
                }
              })
            }
          />
        )}
      </Mutation>
    );
  };

  WrappedComponent.displayName = `withRemoveWorkFromFavorites(${
    PassedComponent.displayName
  })`;

  return WrappedComponent;
};

const optimisticWorkFavorite = (workId, isFavorited) => ({
  __typename: 'Mutation',
  lists_favAction: {
    __typename: 'lists_FavoriteStatus',
    id: nanoid(),
    isFavorited,
    item: {
      __typename: 'lists_WorkFavorite',
      id: nanoid(),
      workId
    }
  }
});

const idForWork = workId => `Work:${UniqueWithinTypeEncode('Work', workId)}`;

const writableFragment = data => ({
  id: idForWork(get(data, 'lists_favAction.item.workId')),
  fragment: WORK_IS_FAVORITED_FRAGMENT,
  data: {
    __typename: 'Work',
    isFavorited: get(data, 'lists_favAction.isFavorited')
  }
});

export const useWorkFavoriteActions = () => {
  const [favorite] = useMutation(FAVORITE_MUTATION);
  const [unfavorite] = useMutation(UNFAVORITE_MUTATION);

  return {
    addWorkToFavorites: workId =>
      favorite({
        variables: { favorite: { workId } },
        optimisticResponse: optimisticWorkFavorite(workId, true),
        update: (cache, { data }) => cache.writeFragment(writableFragment(data))
      }),
    removeWorkFromFavorites: workId =>
      unfavorite({
        variables: { favorite: { workId } },
        optimisticResponse: optimisticWorkFavorite(workId, false),
        update: (cache, { data }) => cache.writeFragment(writableFragment(data))
      })
  };
};

export const useFavoriteActions = () => {
  const [favorite] = useMutation(FAVORITE_MUTATION);
  const [unfavorite] = useMutation(UNFAVORITE_MUTATION);

  return {
    addToFavorites: toggleFavorite(favorite, true),
    removeFromFavorites: toggleFavorite(unfavorite, false)
  };
};

export default useFavoriteActions;
